import type { DispatchState } from 'common/types/React';
import type { CurrentNavigation } from '../types';
import { NAV_BUTTON_TEST_ID, NAV_LINK_TEST_ID } from './useNavigationItemProps';

const focusElement = (container: Element) =>
  (container.querySelector('a') ?? container.querySelector('button')).focus();

const getMenus = (container: Element) => container.querySelectorAll('ul');

const focusFirstMenuLink = (container: Element) => focusElement(getMenus(container)[0]);

const focusNewMenu = (container: Element) => {
  const menus = getMenus(container);
  const lastMenu = menus[menus.length - 1];
  focusElement(lastMenu);
};

const focusPreviousMenu = (container: Element) => {
  const menus = getMenus(container);
  const previousMenu = menus[menus.length - 2];
  focusElement(previousMenu);
};

const focusLastLink = (container: Element) => {
  const menus = getMenus(container);
  const lastMenu = menus[menus.length - 1];
  const lastItem = lastMenu.querySelector('li:last-of-type');
  focusElement(lastItem);
};

const isMenuItem = (element: Element) =>
  element.getAttribute('data-testid') === NAV_BUTTON_TEST_ID ||
  element.getAttribute('data-testid') === NAV_LINK_TEST_ID;

const isCourtesyItem = (element: Element) => !isMenuItem(element) && !element.closest('header');

const getClosestMenuItem = () => document.activeElement.closest('li');

const navigateDown = (target: HTMLElement, container: Element) => {
  const nextItem = getClosestMenuItem()?.nextElementSibling;
  if (nextItem) {
    return focusElement(nextItem);
  }
  if (isMenuItem(target)) {
    // Navigate to the Courtesy section from the first menu
    return focusNewMenu(container);
  }
  focusFirstMenuLink(container);
};

const navigateUp = (
  target: HTMLElement,
  container: Element,
  currentNavigation: CurrentNavigation
) => {
  const previousItem = getClosestMenuItem()?.previousElementSibling;
  if (previousItem) {
    return focusElement(previousItem);
  }
  if (currentNavigation.length === 0 && isCourtesyItem(target)) {
    // Navigate to the first menu from Courtesy section
    const lastItem = getMenus(container)[0].querySelector('li:last-of-type');

    return focusElement(lastItem);
  }
  focusLastLink(container);
};

const navigateRight = (event: KeyboardEvent, container: Element) => {
  event.preventDefault();
  const initialMenusLength = getMenus(container).length;
  (event.target as HTMLElement).click();

  // Wait until new menu is rendered, then focus it
  const interval = 200;
  const focusNewMenuWhenVisible = () => {
    const menus = getMenus(container);
    if (menus.length > initialMenusLength) {
      focusNewMenu(container);
    } else {
      setTimeout(focusNewMenuWhenVisible, interval);
    }
  };

  setTimeout(focusNewMenuWhenVisible, interval);
};

const navigateLeft = (
  currentNavigation: CurrentNavigation,
  setCurrentNavigation: DispatchState<CurrentNavigation>,
  container: Element
) => {
  setCurrentNavigation(currentNavigation.slice(0, -1));
  if (currentNavigation.length > 1) {
    return focusPreviousMenu(container);
  }
  focusFirstMenuLink(container);
};

export { navigateDown, navigateUp, navigateRight, navigateLeft };
